import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, DollarSign, TrendingUp, Wallet, Clock, ArrowUpRight, BarChart3 } from 'lucide-react';
import axios from 'axios';

function StatCard({ label, value, icon: Icon, color = '#FF3B30', prefix = '$' }) {
  return (
    <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono text-gray-500 uppercase tracking-widest">{label}</span>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="text-2xl font-bold text-white font-mono">{prefix}{typeof value === 'number' ? value.toFixed(2) : value}</p>
    </div>
  );
}

export default function StreamerEarnings() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [sumRes, histRes, payRes] = await Promise.all([
        axios.get(`${API}/earnings/summary`, { headers }),
        axios.get(`${API}/earnings/history`, { headers }),
        axios.get(`${API}/payouts/my`, { headers }),
      ]);
      setSummary(sumRes.data);
      setHistory(histRes.data);
      setPayouts(payRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const requestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await axios.post(`${API}/payouts/request`, { amount }, { headers });
      toast.success('Payout requested!');
      setShowPayoutDialog(false);
      setPayoutAmount('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Request failed'); }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" /></div>;

  const maxBar = Math.max(...(summary?.chartData?.map(d => d.earnings) || [1]));

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button data-testid="back-to-home" variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white hover:bg-white/5 p-2"><ArrowLeft className="w-4 h-4" /></Button>
            <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-yellow-400" /><span className="font-heading text-lg font-bold text-white uppercase">Earnings</span></div>
          </div>
          <Button data-testid="request-payout-button" onClick={() => setShowPayoutDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm" disabled={!summary || summary.availableBalance <= 0}>
            <ArrowUpRight className="w-4 h-4 mr-1" /> Request Payout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Available Balance" value={summary?.availableBalance || 0} icon={Wallet} color="#10B981" />
          <StatCard label="This Month" value={summary?.monthNet || 0} icon={TrendingUp} color="#3B82F6" />
          <StatCard label="This Week" value={summary?.weekNet || 0} icon={BarChart3} color="#F59E0B" />
          <StatCard label="Total Earned" value={summary?.totalNet || 0} icon={DollarSign} color="#FF3B30" />
        </div>

        {summary?.guarantee ? (
          <div data-testid="guarantee-banner" className={`mb-8 rounded-lg border p-5 ${summary.guarantee.active ? 'border-[#FF3B30]/25 bg-[#FF3B30]/[0.05]' : 'border-white/10 bg-white/[0.02]'}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30]">Guaranteed Rate</span>
                  {!summary.guarantee.active && <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/10 text-gray-400">{(summary.guarantee.status || 'paused').replace('_', ' ')}</span>}
                </div>
                <p className="text-3xl font-mono font-black text-white leading-none">${(summary.guarantee.guaranteedCpm || 0).toFixed(2)} <span className="text-sm text-gray-500 font-bold">CPM</span></p>
                <p className="text-xs text-gray-400 mt-1">Paid to you in full on every video ad — no platform split.{summary.guarantee.endDate ? ` Guaranteed through ${summary.guarantee.endDate}.` : ''}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-mono font-black text-[#FF3B30] leading-none">${(summary.guarantee.guaranteedEarnings || 0).toFixed(2)}</p>
                <p className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mt-1">Earned at guaranteed rate</p>
                <p className="text-[10px] text-gray-600 font-mono">{summary.guarantee.deliveries || 0} spots delivered</p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Additional Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-4 text-center">
            <p className="text-2xl font-bold text-white font-mono">{summary?.totalImpressions || 0}</p>
            <p className="text-xs text-gray-500 font-mono uppercase">Total Impressions</p>
          </div>
          <div data-testid="scans-driven-card" className="border border-[#FF3B30]/25 rounded-lg bg-[#FF3B30]/[0.04] p-4 text-center">
            <p className="text-2xl font-bold text-[#FF3B30] font-mono">{summary?.scansDriven || 0}</p>
            <p className="text-xs text-gray-500 font-mono uppercase">QR Scans Driven</p>
            {summary?.scansThisMonth ? <p className="text-[10px] text-gray-600 font-mono mt-1">{summary.scansThisMonth} this month</p> : null}
          </div>
          <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-4 text-center">
            <p className="text-2xl font-bold text-white font-mono">${(summary?.paidOut || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 font-mono uppercase">Paid Out</p>
          </div>
          <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-4 text-center">
            <p className="text-2xl font-bold text-white font-mono">${(summary?.pendingPayouts || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-500 font-mono uppercase">Pending Payouts</p>
          </div>
        </div>

        {summary?.scansByBrand?.length ? (
          <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6 mb-8">
            <h3 className="font-heading text-lg font-bold text-white mb-1">Scans You Drove</h3>
            <p className="text-xs text-gray-500 mb-4">Viewers who scanned a sponsor's QR code during your breaks — brands see this attributed to you.</p>
            <div className="space-y-2">
              {summary.scansByBrand.map(b => (
                <div key={b.brand} className="flex items-center justify-between p-2 rounded border border-white/5 bg-white/[0.02]">
                  <span className="text-sm text-white">{b.brand}</span>
                  <span className="text-sm font-mono text-[#FF3B30] font-bold">{b.scans}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Earnings Chart */}
        <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6 mb-8">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Last 30 Days</h3>
          <div className="flex items-end gap-[2px] h-32">
            {summary?.chartData?.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center group relative">
                <div className="w-full bg-[#FF3B30]/60 rounded-t-sm transition-all hover:bg-[#FF3B30]"
                  style={{ height: `${maxBar > 0 ? (d.earnings / maxBar) * 100 : 0}%`, minHeight: d.earnings > 0 ? '2px' : '0' }} />
                <div className="absolute bottom-full mb-1 bg-black/90 border border-white/10 rounded px-2 py-1 text-xs text-white font-mono opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  {d.date}: ${d.earnings.toFixed(4)}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-gray-600 font-mono">{summary?.chartData?.[0]?.date}</span>
            <span className="text-xs text-gray-600 font-mono">{summary?.chartData?.[summary.chartData.length - 1]?.date}</span>
          </div>
        </div>

        {/* Two Column: History + Payouts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Earnings History */}
          <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
            <h3 className="font-heading text-lg font-bold text-white mb-4">Earnings History</h3>
            {history.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No earnings yet. Trigger ads from approved placements to earn.</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {history.map(e => (
                  <div key={e.id} className="flex items-center justify-between p-3 bg-white/[0.02] rounded border border-white/5">
                    <div>
                      <p className="text-sm text-white">{e.adFilename || 'Ad'}</p>
                      <p className="text-xs text-gray-500 font-mono">{e.brandName} &middot; ${e.bidCpm?.toFixed(2)} CPM</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-emerald-400 font-mono">+${e.netEarning?.toFixed(4)}</p>
                      <p className="text-xs text-gray-600 font-mono">{new Date(e.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payout History */}
          <div className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
            <h3 className="font-heading text-lg font-bold text-white mb-4">Payout History</h3>
            {payouts.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No payout requests yet.</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {payouts.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-white/[0.02] rounded border border-white/5">
                    <div>
                      <p className="text-sm text-white font-mono">${p.amount?.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 font-mono">{new Date(p.requestedAt).toLocaleDateString()}</p>
                    </div>
                    <Badge className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wider font-mono border ${
                      p.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                      p.status === 'processing' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                      'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                    }`}>{p.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 p-4 border border-white/10 rounded-lg bg-[#121212]/50 text-center">
          <p className="text-xs text-gray-500 font-mono">Platform fee: 30% &middot; Streamer revenue share: 70% &middot; Earnings based on CPM from approved placements</p>
        </div>
      </main>

      {/* Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="text-white font-heading">Request Payout</DialogTitle><DialogDescription className="text-gray-500">Available: ${summary?.availableBalance?.toFixed(2)}</DialogDescription></DialogHeader>
          <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Amount ($)</Label><Input data-testid="payout-amount-input" type="number" step="0.01" value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)} className="bg-black/40 border-white/10 text-white mt-1" placeholder="0.00" /></div>
          <DialogFooter><Button data-testid="submit-payout-button" onClick={requestPayout} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold w-full"><ArrowUpRight className="w-4 h-4 mr-1" /> Request Payout</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
